<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useResourceSummaryStore } from '@/stores/resourceSummary.store';
import { useSessionStore } from '@/stores/session.store';
import { assetUrl } from '@/utils/assetUrl';

/**
 * Barre de progression du joueur (sous le menu global), DEUX pistes empilées
 * pour rester compactes :
 *  - NIVEAU (TotalScore) : badge de tranche (image SDXL `level_badges/tier_N`,
 *    repli pastille CSS) + barre cyan vers le niveau suivant ;
 *  - COMBAT (rang militaire, PointBattle) : icône de rang (`ranks/<code>`) +
 *    barre ambre vers le rang suivant.
 * Chaque barre surligne le dernier gain (segment plus clair + pulse).
 * Source = `resourceSummary` (payload /me) : déjà pollé 15s + rafraîchi sur
 * onTaskResolved par AppLayout → une seule source joueur, aucun fetch propre ici.
 */
const store = useResourceSummaryStore();
const session = useSessionStore();

function fmt(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
}

// --- Niveau ---
const badgeImgError = ref(false);
const badgeUrl = computed(() => assetUrl('level_badges', `tier_${store.badgeTier}`, 0));
watch(() => store.badgeTier, () => { badgeImgError.value = false; });

const lvlBasePct = computed(() => Math.round(store.levelPrevProgress * 100));
const lvlHasGain = computed(() => store.recentGain > 0);
// Largeur du segment de gain : min 3 % quand il y a un gain (sinon un petit gain
// arrondi à 0 % serait invisible), borné pour ne pas déborder.
const lvlRecentPct = computed(() => {
  if (!lvlHasGain.value) return 0;
  const raw = Math.round((store.levelProgress - store.levelPrevProgress) * 100);
  return Math.min(Math.max(0, 100 - lvlBasePct.value), Math.max(3, raw));
});
const xpInLevel = computed(() => store.totalScore - store.levelStartScore);
const xpForLevel = computed(() => Math.max(1, store.levelNextScore - store.levelStartScore));

// --- Combat / rang ---
const rankImgError = ref(false);
const rankIconUrl = computed(() => store.rankImageCode ? assetUrl('ranks', store.rankImageCode, 0) : null);
watch(() => store.rankImageCode, () => { rankImgError.value = false; });

const isMaxRank = computed(() => store.rankNextScore <= store.rankFloorScore);
const cbtBasePct = computed(() => Math.round(store.combatPrevProgress * 100));
const cbtHasGain = computed(() => store.recentCombatGain > 0 && !isMaxRank.value);
const cbtRecentPct = computed(() => {
  if (!cbtHasGain.value) return 0;
  const raw = Math.round((store.combatProgress - store.combatPrevProgress) * 100);
  return Math.min(Math.max(0, 100 - cbtBasePct.value), Math.max(3, raw));
});
const cbtInRank = computed(() => store.combatScore - store.rankFloorScore);
const cbtForRank = computed(() => Math.max(1, store.rankNextScore - store.rankFloorScore));
const rankText = computed(() => store.rankLabel ?? 'Sans rang');
</script>

<template>
  <div v-if="session.inUniverse" class="player-level-bar" role="status" aria-live="polite">
    <!-- Badge de niveau (tranche de 10) : image SDXL ou pastille CSS. Cliquable
         -> Résonance (progression post-cap niveau 50). -->
    <router-link to="/resonance" class="plb-badge" :title="`Niveau ${store.level} — ✦ Résonance`">
      <img
        v-if="!badgeImgError"
        :src="badgeUrl"
        class="plb-badge-img"
        alt=""
        loading="lazy"
        @error="badgeImgError = true"
      />
      <span class="plb-badge-level">{{ store.level }}</span>
    </router-link>

    <div class="plb-stack">
      <!-- Ligne NIVEAU -->
      <div class="plb-row">
        <div
          class="plb-track"
          :title="`${fmt(xpInLevel)} / ${fmt(xpForLevel)} XP vers le niveau ${store.level + 1}`"
        >
          <div class="plb-fill plb-fill--lvl" :style="{ width: lvlBasePct + '%' }" />
          <div
            v-if="lvlHasGain"
            class="plb-fill plb-fill--recent"
            :style="{ left: lvlBasePct + '%', width: lvlRecentPct + '%' }"
          />
          <span class="plb-track-text">{{ fmt(xpInLevel) }} / {{ fmt(xpForLevel) }}</span>
        </div>
        <Transition name="plb-gain">
          <span v-if="lvlHasGain" class="plb-gain plb-gain--lvl">+{{ fmt(store.recentGain) }}</span>
        </Transition>
      </div>

      <!-- Ligne COMBAT / RANG -->
      <div class="plb-row">
        <div
          class="plb-track plb-track--combat"
          :title="isMaxRank ? `${rankText} (rang max)` : `${rankText} — ${fmt(cbtInRank)} / ${fmt(cbtForRank)} vers le rang suivant`"
        >
          <div class="plb-fill plb-fill--cbt" :style="{ width: (isMaxRank ? 100 : cbtBasePct) + '%' }" />
          <div
            v-if="cbtHasGain && !isMaxRank"
            class="plb-fill plb-fill--recent"
            :style="{ left: cbtBasePct + '%', width: cbtRecentPct + '%' }"
          />
          <span class="plb-track-text">
            {{ rankText }}<template v-if="!isMaxRank"> · {{ fmt(cbtInRank) }} / {{ fmt(cbtForRank) }}</template>
          </span>
        </div>
        <Transition name="plb-gain">
          <span v-if="cbtHasGain" class="plb-gain plb-gain--cbt">+{{ fmt(store.recentCombatGain) }}</span>
        </Transition>
      </div>
    </div>

    <!-- Badge de RANG (même taille que le badge de niveau), icône SDXL ranks/<code>
         (repli icône Médaille). Tooltip = libellé du rang. -->
    <div class="plb-badge plb-badge--rank" :title="rankText">
      <img
        v-if="rankIconUrl && !rankImgError"
        :src="rankIconUrl"
        class="plb-badge-img"
        alt=""
        loading="lazy"
        @error="rankImgError = true"
      />
      <el-icon v-else :size="22"><Medal /></el-icon>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.player-level-bar {
  display: flex;
  align-items: center;
  gap: $space-3;
  padding: 3px $space-4;
  background: linear-gradient(90deg, rgba($color-neon-cyan-bright, 0.06), rgba($accent-legendary, 0.04));
  border-bottom: 1px solid $border-subtle;

  @media (max-width: 767.99px) {
    gap: $space-2;
    padding: 3px $space-2;
  }
}

// Badge de niveau circulaire (image SDXL + numéro), s'aligne sur la hauteur des 2 barres.
.plb-badge {
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: $radius-full;
  background: radial-gradient(circle at 50% 35%, rgba($color-neon-cyan-bright, 0.30), rgba($accent-legendary, 0.20));
  border: 1px solid rgba($color-neon-cyan-bright, $alpha-medium);
  box-shadow: 0 0 10px rgba($color-neon-cyan-bright, 0.25);
}
.plb-badge-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: $radius-full;
}
.plb-badge-level {
  position: relative;
  z-index: 1;
  font-family: $font-display;
  font-size: $font-size-sm;
  font-weight: $font-weight-bold;
  color: $text-primary;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  line-height: 1;
}

// Badge de RANG : même gabarit que le badge de niveau, teinte ambre (combat).
.plb-badge--rank {
  color: $color-warning;
  background: radial-gradient(circle at 50% 35%, rgba($color-warning, 0.30), rgba($color-danger, 0.18));
  border-color: rgba($color-warning, $alpha-medium);
  box-shadow: 0 0 10px rgba($color-warning, 0.25);
}

// Pile des 2 barres (niveau + combat).
.plb-stack {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.plb-row {
  display: flex;
  align-items: center;
  gap: $space-2;
  min-width: 0;
}

// Barre (commune niveau + combat) — fine pour rester compacte.
.plb-track {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  height: 13px;
  border-radius: $radius-full;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid $border-subtle;
  overflow: hidden;
}
.plb-fill {
  position: absolute;
  top: 0;
  bottom: 0;
  transition: width $duration-normal $easing-default, left $duration-normal $easing-default;
}
.plb-fill--lvl {
  left: 0;
  background: linear-gradient(90deg, rgba($color-neon-cyan, 0.55), rgba($color-neon-cyan-bright, 0.85));
}
.plb-fill--cbt {
  left: 0;
  background: linear-gradient(90deg, rgba($color-danger, 0.55), rgba($color-warning, 0.9));
}
// Segment du dernier gain : OR VIF + halo, couleur volontairement distincte des
// deux barres (cyan niveau / ambre combat) pour qu'on voie « ce qui vient d'être
// gagné ». Liseré clair à gauche pour marquer la jonction.
.plb-fill--recent {
  background: linear-gradient(90deg, #ffd24a, #fff6c2);
  box-shadow: 0 0 8px rgba(#ffd24a, 0.9), inset 1px 0 0 rgba(#fff, 0.9);
  animation: plb-pulse 1s ease-in-out infinite;
}
@keyframes plb-pulse {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
}
.plb-track-text {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-family: $font-mono;
  font-size: 9px;
  letter-spacing: 0.02em;
  color: $text-primary;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
}

.plb-gain {
  flex: 0 0 auto;
  font-family: $font-mono;
  font-size: 11px;
  font-weight: $font-weight-bold;
  &.plb-gain--lvl { color: $color-neon-cyan; }
  &.plb-gain--cbt { color: $color-warning; }
}
.plb-gain-enter-active { transition: all $duration-normal $easing-default; }
.plb-gain-enter-from { opacity: 0; transform: translateX(-6px); }
</style>
